import { forwardRef } from 'react';
import { Button, ButtonProps } from 'react-aria-components';
import { cva, VariantProps } from 'cva';
import { twMerge } from 'tailwind-merge';

import { IconType } from 'types/icon';

import { Icon } from 'components/Icon/Icon';

const iconButton = cva({
  base: 'group outline-lumeo-500 flex items-center justify-center rounded-full outline-0 outline-offset-1 transition focus-visible:outline-2',
  variants: {
    variant: {
      primary: 'bg-lumeo-500',
      secondary: 'border-1 border-neutral-200 dark:border-neutral-700',
      ghost: '',
      destructive: 'border-1 border-neutral-200 dark:border-neutral-700',
    },
    size: {
      sm: 'h-6 w-6',
      md: 'h-8 w-8',
    },
    isDisabled: { true: 'opacity-50' },
  },
  compoundVariants: [
    {
      variant: 'primary',
      isDisabled: false,
      class: 'hover:bg-lumeo-600',
    },
    {
      variant: 'secondary',
      isDisabled: false,
      class: 'hover:bg-neutral-100 dark:hover:bg-neutral-700',
    },
    {
      variant: 'ghost',
      isDisabled: false,
      class: 'hover:bg-neutral-200 dark:hover:bg-neutral-700',
    },
    {
      variant: 'destructive',
      isDisabled: false,
      class:
        'hover:border-red-200 hover:bg-red-100 dark:hover:bg-transparent dark:hover:border-red-400',
    },
  ],
  defaultVariants: { variant: 'ghost', size: 'md' },
});

const icon = cva({
  variants: {
    variant: {
      primary: 'text-neutral-100',
      secondary: 'text-neutral-600 dark:text-neutral-300',
      ghost: 'text-neutral-600 dark:text-neutral-300',
      destructive: 'text-neutral-600 dark:text-neutral-300',
    },
    isDisabled: { true: '' },
  },
  compoundVariants: [
    {
      variant: 'destructive',
      isDisabled: false,
      class: 'group-hover:text-red-500 dark:group-hover:text-red-400',
    },
  ],
});

const iconSize = { sm: 'xs', md: 'sm' } as const;

type IconButtonProps = ButtonProps &
  VariantProps<typeof iconButton> & { icon: IconType };

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon: name,
      size = 'md',
      variant,
      className,
      isDisabled = false,
      ...props
    },
    ref
  ) => {
    return (
      <Button
        {...props}
        className={(props) =>
          twMerge(
            iconButton({ variant, size, isDisabled }),
            typeof className === 'function' ? className(props) : className
          )
        }
        isDisabled={isDisabled}
        ref={ref}
      >
        <Icon
          name={name}
          size={iconSize[size]}
          className={icon({ variant, isDisabled })}
        />
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';
